if (!customElements.get('buy-it-now-form')) {
  customElements.define(
    'buy-it-now-form',
    class BuyItNowForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput = this.form.querySelector('[name="id"]');
        this.quantityInput = this.form.querySelector('[name="quantity"]');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
      }

      get variantIdInput() {
        return this.form.querySelector('[name="id"]');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        
        // Get variant ID and quantity
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;

        // Validate variant availability before proceeding
        this.validateAndBuyNow(variantId, quantity);
      }

      async validateAndBuyNow(variantId, quantity) {
        try {
          // First, add to cart
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', quantity);
          
          config.body = formData;

          const response = await fetch(`${routes.cart_add_url}`, config);
          const data = await response.json();

          if (data.status) {
            // Handle error
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'buy-it-now-form',
              productVariantId: variantId,
              errors: data.errors || data.description,
              message: data.message,
            });
            this.handleErrorMessage(data.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            this.error = true;
            
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            return;
          }

          // Success - publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'buy-it-now-form',
            productVariantId: variantId,
            cartData: data,
          });

          // Redirect to checkout
          window.location = window.routes.cart_url + '?checkout';
          
          // Note: Keep loading state until redirect completes
          // Don't remove loading state here as we're navigating away
          
        } catch (error) {
          console.error('Buy It Now Error:', error);
          this.handleErrorMessage('An error occurred. Please try again.');
          
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        const errorMessageWrapper = this.querySelector('.buy-it-now-form__error-message-wrapper');
        if (!errorMessageWrapper) return;
        
        if (errorMessage) {
          errorMessageWrapper.querySelector('.buy-it-now-form__error-message').innerText = errorMessage;
          errorMessageWrapper.hidden = false;
        } else {
          errorMessageWrapper.hidden = true;
        }
      }

      // Listen for variant changes from the main product form
      connectedCallback() {
        // Listen to variant change events
        document.addEventListener('variant:change', (event) => {
          this.updateVariant(event.detail);
        });

        // Also listen to the main product form's variant input changes
        const mainProductForm = document.querySelector('product-form');
        if (mainProductForm) {
          const mainVariantInput = mainProductForm.querySelector('[name="id"]');
          if (mainVariantInput) {
            // Sync variant ID
            const observer = new MutationObserver((mutations) => {
              mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                  this.variantIdInput.value = mainVariantInput.value;
                  this.variantIdInput.disabled = mainVariantInput.disabled;
                  this.updateButtonState();
                }
              });
            });
            
            observer.observe(mainVariantInput, { attributes: true });
            
            // Also listen for value changes via input event
            mainVariantInput.addEventListener('change', () => {
              this.variantIdInput.value = mainVariantInput.value;
              this.variantIdInput.disabled = mainVariantInput.disabled;
              this.updateButtonState();
            });
          }

          // Sync quantity
          const mainQuantityInput = mainProductForm.querySelector('[name="quantity"]');
          if (mainQuantityInput) {
            mainQuantityInput.addEventListener('change', () => {
              this.quantityInput.value = mainQuantityInput.value;
            });
            
            // Also handle quantity selector updates
            mainQuantityInput.addEventListener('input', () => {
              this.quantityInput.value = mainQuantityInput.value;
            });
          }
        }

        // Listen for quantity selector changes
        const quantitySelector = document.querySelector('quantity-input');
        if (quantitySelector) {
          quantitySelector.addEventListener('change', (event) => {
            const newQuantity = event.target.value || event.detail?.quantity;
            if (newQuantity) {
              this.quantityInput.value = newQuantity;
            }
          });
        }
      }

      updateVariant(variant) {
        if (!variant) return;
        
        this.variantIdInput.value = variant.id;
        this.variantIdInput.disabled = !variant.available;
        this.updateButtonState();
      }

      updateButtonState() {
        const isDisabled = this.variantIdInput.disabled || !this.variantIdInput.value;
        
        if (isDisabled) {
          this.submitButton.setAttribute('disabled', true);
          this.submitButton.setAttribute('aria-disabled', true);
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
        }

        // Update button text based on availability
        const variant = this.getCurrentVariant();
        if (variant) {
          const buttonTextSpan = this.submitButtonText;
          if (!variant.available) {
            buttonTextSpan.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1L8 11M8 11L5 8M8 11L11 8M2 15H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${this.translations?.soldOut || 'Sold Out'}
            `;
          } else {
            buttonTextSpan.innerHTML = `
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 1L8 11M8 11L5 8M8 11L11 8M2 15H14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${this.translations?.buyNow || 'Buy It Now'}
            `;
          }
        }
      }

      getCurrentVariant() {
        // Try to get variant info from the main product form
        const mainForm = document.querySelector('product-form');
        if (mainForm && mainForm.currentVariant) {
          return mainForm.currentVariant;
        }
        return null;
      }

      get translations() {
        return {
          soldOut: this.dataset.soldOutText || 'Sold Out',
          buyNow: this.dataset.buyNowText || 'Buy It Now',
          unavailable: this.dataset.unavailableText || 'Unavailable',
        };
      }
    }
  );
}
